import { NgModule, inject } from '@angular/core';
import { Routes, RouterModule, CanActivateFn, Router, UrlSegment, ActivatedRouteSnapshot } from '@angular/router';
import { GuestGuard } from '../core/guards/guest.guard';

import { LoginV2Component } from '../login/loginV2/login.component';
import { ForgotPasswordComponent } from '../login/forgot-password/forgot-password.component';
import { LandingComponent } from '../unattached/landing/landing.component';
import { SignupV2Component } from '../login/signupV2/signup.component';
import { ReceiptComponent } from '../unattached/receipt/receipt.component';
import { OrderReceiptComponent } from '../unattached/order-receipt/order-receipt.component';
import { SupportComponent } from '../unattached/support/support.component';
import { ConfirmAccountComponent } from '../login/confirm-account/confirm-account.component';
import { TermsComponent } from '../unattached/terms/terms.component';

import { ConfirmAuthComponent } from '../author-dashboard/confirm-auth/confirm-auth.component';
import { MembershipComponent } from '../membership/membership.component';
import { AuthorizeAppComponent } from '../author-dashboard/authorize-app/authorize-app.component';

import { EmptyComponent } from './empty.component';
import { UserService } from '../core/services/data/user.service';
import { UtilitiesService } from '../core/services/utilities.service';

export const redirectGuard = (route: ActivatedRouteSnapshot) => {
	const userService = inject(UserService);
	const utilitiesService = inject(UtilitiesService);
	const router = inject(Router);
	const segments: string[] = route.url.map((seg: UrlSegment) => seg.path);

	return userService.fetchUser().then(
		(user: any) => router.createUrlTree(['/author-dashboard', user.authorLink, ...segments]),
		() => {
			// Login resumes via `/author-dashboard/${authorLink}/${route}`, so save a path string, not the segment array
			utilitiesService.saveRoute(segments.join('/'));
			return router.createUrlTree(['/login']);
		},
	);
};

export const loginGuardFn = (urlParts: string[]): CanActivateFn => () => {
	const userService = inject(UserService);
	const router = inject(Router);

	return userService.fetchUser().then(
		(user: any) => router.createUrlTree(['/author-dashboard', user.authorLink, ...urlParts]),
		() => true,
	);
};

export const routes: Routes = [
	{ path: '', component: LandingComponent },
	{
		path: 'home',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'account',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'account-contact-info',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'author-profile',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'account-login-security',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'billing',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'account/options',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'promotions',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'books',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'alerts',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'subscriptions-code',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'exclusives',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'resources',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'discounts',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'promo-wizard',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'events',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'promotions/:promoId',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'ai-toolkit/social-writer',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'ai-toolkit/book-description',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},

	{ path: 'subscriptions', redirectTo: '/membership' },
	{ path: 'subscriptions/:membershipLevel', redirectTo: '/membership' },
	{
		path: 'author-dashboard',
		pathMatch: 'full',
		canActivate: [redirectGuard],
		component: EmptyComponent,
	},
	{
		path: 'author-dashboard',
		loadChildren: () => import('../author-dashboard/author-dashboard.module').then((m) => m.AuthorDashboardModule),
	},
	{ path: 'confirm-account/:author/:code', component: ConfirmAccountComponent, data: { title: 'Confirm Account' } },
	{
		path: 'features',
		loadChildren: () => import('../features/features.module').then((m) => m.FeaturesModule),
		canActivate: [GuestGuard],
	},
	{ path: 'features2', redirectTo: '/features' },
	{
		path: 'promo-stacks',
		loadChildren: () => import('../promo-stacks/promo-stacks.module').then((m) => m.PromoStackModule),
		canActivate: [GuestGuard],
	},
	{
		path: 'events',
		loadChildren: () => import('../events/events.module').then((m) => m.EventsModule),
		canActivate: [GuestGuard],
	},
	{ path: 'forgot-password', component: ForgotPasswordComponent, data: { title: 'Forgot Password' } },
	{
		path: 'login',
		component: LoginV2Component,
		data: { title: 'Login' },
		canActivate: [loginGuardFn(['home'])],
	},
	{ path: 'receipt/:feature', component: ReceiptComponent, data: { title: 'Receipt' } },
	{ path: 'order-receipt/:order', component: OrderReceiptComponent, data: { title: 'Order Receipt' } },
	{ path: 'reset-password/:authorLink/:resetCode', component: ForgotPasswordComponent, data: { title: 'Reset Password' } },
	{ path: 'signup', component: SignupV2Component, data: { title: 'Signup' } },
	{ path: 'support', component: SupportComponent, data: { title: 'Support' } },
	{ path: 'terms', component: TermsComponent, data: { title: 'Terms of Service' } },
	{ path: 'confirm-auth', component: ConfirmAuthComponent, data: { title: 'Auth Confirmation' } },
	{ path: 'authorize-app', component: AuthorizeAppComponent, data: { title: 'Authorize App' } },
	{ path: 'membership', component: MembershipComponent, data: { title: 'Membership' } },
	{ path: '**', redirectTo: '/features' },
];

@NgModule({
	imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'top' })],
	exports: [RouterModule],
})
export class AppRoutingModule {

}
